// Which "needs you" rows need a person the most (Neila 2026-10-08: "by SI, PL, or injuries ... pull notes, like brain bleed, coma, other
// significant injuries"). Transparent points, one chip per signal with the reason; every row still shows, only the order changes.
// Tune with Neila by editing PRIORITY_RULES: the "How we rank" popover reads this same table.
export const PRIORITY_RULES = {
    reply: { points: 100, what: 'The client replied to our text and is waiting for us (Rod 10-08): a waiting client comes first' },
    si: { points: 40, what: 'Serious Injury is checked on the case' },
    severe: { points: 40, what: 'A severe injury in the injuries or a staff note (Neila 10-08): skull fracture, brain bleed or hemorrhage, coma, or a hospital stay of 5 days or more' },
    tierHigh: { points: 25, what: 'Injury Tier 3 or 4' },
    surgery: { points: 20, what: 'A named surgery (Neila 10-08): ORIF, fusion, ACDF or disc replacement, or a surgery field is filled; the word surgery alone does not count' },
    pl: { points: 15, what: 'A known policy limit over $250k (Neila 10-08), or a policy-limits action in a note (PL tender, limits demand, policy limits offer)' },
    tierMid: { points: 10, what: 'Injury Tier 2' },
};
export const PRIORITY_TIEBREAK = 'Equal points: the longest wait first';
const HIGH_LIMIT = 250_000; // strictly over (Neila 10-08: "PL >250")
const SEVERE = [
    [/\b(brain (bleed|hemorrhage|haemorrhage)|intracranial (bleed|hemorrhage)|subdural|subarachnoid)\b/i, 'brain bleed'],
    [/\b(skull fracture|fractured skull|basilar fracture)\b/i, 'skull fracture'],
    [/\b(coma|comatose)\b/i, 'coma'],
];
// A hospital stay of 5 days or more (Neila 10-08): "admitted for 7 days", "hospitalized for 2 weeks", "a 5-day hospital stay",
// "six days in the hospital", or admit / discharge dates ("admitted 9/1, discharged 9/8").
const NUM = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14 };
const N = '(\\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen)';
const STAY = [
    new RegExp(`\\b(?:admitted|hospitali[sz]ed|hospitali[sz]ation|inpatient|in the hospital|in hospital)\\b[^.;]{0,30}?\\b${N}\\s*(?:\\+\\s*)?(days?|weeks?)\\b`, 'i'),
    new RegExp(`\\b${N}[- ](day|week)s?\\s+(?:hospital |inpatient )?(?:stay|admission)\\b`, 'i'),
    new RegExp(`\\b${N}\\s*(days?|weeks?)\\s+(?:in|at) (?:the )?(?:hospital|icu|inpatient)\\b`, 'i'),
];
const ADMIT_DISCHARGE = /\badmit(?:ted)?\s*(?:on\s*)?(\d{1,2})\/(\d{1,2})[^.]{0,60}?\bdischarg\w*\s*(?:on\s*)?(\d{1,2})\/(\d{1,2})/i;
// a stay from the prior-history section is not this crash's ("Prior accidents: broken wrist last March, stayed in hospital for 8 days")
const PRIOR = /\b(prior|previous|previously|history|last (year|month|january|february|march|april|may|june|july|august|september|october|november|december)|years? ago|in (19|20)\d\d)\b/i;
function stayDays(text) {
    for (const re of STAY) {
        const m = re.exec(text);
        if (!m)
            continue;
        const i = m.index;
        const sentenceStart = Math.max(text.lastIndexOf('.', i - 1), text.lastIndexOf(';', i - 1)) + 1;
        if (NEG_BEFORE.test(text.slice(sentenceStart, i + 1)))
            continue;
        if (PRIOR.test(text.slice(Math.max(0, i - 60), i + m[0].length + 20)))
            continue;
        const n = /^\d+$/.test(m[1]) ? Number(m[1]) : NUM[m[1].toLowerCase()] ?? 0;
        return /week/i.test(m[2]) ? n * 7 : n;
    }
    const d = ADMIT_DISCHARGE.exec(text);
    if (d) {
        const a = Date.UTC(2026, Number(d[1]) - 1, Number(d[2])), b = Date.UTC(2026, Number(d[3]) - 1, Number(d[4]));
        const days = Math.round((b - a) / 86400000);
        if (days > 0 && days < 120)
            return days;
    }
    return null;
}
const SURGERY = /\b(orif|fusion|acdf|disc replacement)\b/i;
// Only an action counts: the bare words "policy limits" are in the intake call script ("Case timeline depends on the policy limits we are
// working with", "c. Policy limits"), so they matched about 1 case in 3 on 10-08 without saying anything about this case.
const PL_WORDS = /\b(pl (tender|demand|offer)|(policy[- ])?limits (demand|tender|offer)|tender(ed)? (of )?(the )?(policy )?limits|offered (the )?(policy )?limits|policy[- ]limits (case|claim))\b/i;
// The intake note template's own labels: not findings ("TBI Questions: (if applicable)", "Airbags Deployed(TBI): no").
// Also the call script ("Unless you are bleeding on the floor or in a coma, to adjuster you are NOT injured") and the head-to-toe form label.
const TEMPLATE_NOISE = [/\bTBI Questions:?\s*(\(if applicable\))?/gi, /\(\s*TBI\s*\)/gi, /\bunless you are bleeding on the floor or in a coma\b/gi,
    /\bClosed Head Injury\s*\/\s*TBI Assessment:?/gi];
const NEG_BEFORE = /\b(no|not|denies|denied|deny|without|negative for|ruled out|r\/o|non)\b[^.;\n]{0,25}$/i;
const NEG_AFTER = /^[\s:,-]{0,3}(not (needed|required|recommended)|none|no\b|denied)/i;
const clean = (t) => TEMPLATE_NOISE.reduce((x, re) => x.replace(re, ' '), t.replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&#160;/g, ' ').replace(/&amp;/g, '&').replace(/&#39;/g, "'")).replace(/\s+/g, ' ');
/** The first match of `re` in `text` that is not negated in its own sentence ("no surgery", "denies LOC", "not intubated"). */
function firstReal(text, re) {
    const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
    for (const m of text.matchAll(g)) {
        const i = m.index ?? 0;
        const sentenceStart = Math.max(text.lastIndexOf('.', i - 1), text.lastIndexOf(';', i - 1), text.lastIndexOf('\n', i - 1)) + 1;
        const before = text.slice(sentenceStart, i);
        const after = text.slice(i + m[0].length, i + m[0].length + 20);
        if (NEG_BEFORE.test(before) || NEG_AFTER.test(after))
            continue;
        return m[0];
    }
    return null;
}
const money = (n) => (n >= 1_000_000 ? `$${+(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n / 1000)}k`);
const noteDay = (at) => (at ? ` ${Number(at.slice(5, 7))}/${Number(at.slice(8, 10))}` : '');
export function scorePriority(p) {
    const chips = [];
    const sources = [];
    if (p.injuries)
        sources.push({ text: clean(p.injuries), detail: 'intake injuries' });
    for (const n of p.notes) {
        const t = typeof n === 'string' ? { text: n } : n;
        if (t.text)
            sources.push({ text: clean(t.text), detail: `staff note${noteDay(t.at)}` });
    }
    if (p.replyWaiting)
        chips.push({ key: 'reply', label: 'client replied', points: PRIORITY_RULES.reply.points, detail: PRIORITY_RULES.reply.what });
    if (p.si)
        chips.push({ key: 'si', label: 'SI', points: PRIORITY_RULES.si.points, detail: PRIORITY_RULES.si.what });
    severe: for (const s of sources) {
        for (const [re, name] of SEVERE) {
            const hit = firstReal(s.text, re);
            if (hit) {
                chips.push({ key: 'severe', label: hit.length <= 24 ? hit.toLowerCase() : name, points: PRIORITY_RULES.severe.points, detail: `${name}, from the ${s.detail}` });
                break severe;
            }
        }
        const days = stayDays(s.text);
        if (days !== null && days >= 5) {
            chips.push({ key: 'severe', label: `hospital ${days} days`, points: PRIORITY_RULES.severe.points, detail: `a hospital stay of ${days} days, from the ${s.detail}` });
            break severe;
        }
    }
    const tier = Number(p.tier);
    if (p.tier !== null && tier >= 3)
        chips.push({ key: 'tier', label: `Tier ${tier}`, points: PRIORITY_RULES.tierHigh.points, detail: PRIORITY_RULES.tierHigh.what });
    else if (p.tier !== null && tier === 2)
        chips.push({ key: 'tier', label: 'Tier 2', points: PRIORITY_RULES.tierMid.points, detail: PRIORITY_RULES.tierMid.what });
    let surgery = p.surgeryFields ? 'surgery field' : null;
    let surgeryFrom = 'the surgery fields';
    if (!surgery)
        for (const s of sources) {
            const hit = firstReal(s.text, SURGERY);
            if (hit) {
                surgery = /^(orif|acdf)$/i.test(hit) ? hit.toUpperCase() : hit.toLowerCase();
                surgeryFrom = `the ${s.detail}`;
                break;
            }
        }
    if (surgery)
        chips.push({ key: 'surgery', label: surgery, points: PRIORITY_RULES.surgery.points, detail: `${surgery}, from ${surgeryFrom}` });
    const top = Math.max(0, ...p.limits.filter((x) => Number.isFinite(x)));
    if (top > HIGH_LIMIT)
        chips.push({ key: 'pl', label: `PL ${money(top)}`, points: PRIORITY_RULES.pl.points, detail: 'the largest known policy limit' });
    else {
        for (const s of sources.filter((x) => x.detail.startsWith('staff note'))) {
            const hit = firstReal(s.text, PL_WORDS);
            if (hit) {
                chips.push({ key: 'pl', label: 'PL', points: PRIORITY_RULES.pl.points, detail: `"${hit.toLowerCase()}" in a ${s.detail}` });
                break;
            }
        }
    }
    chips.sort((a, b) => b.points - a.points);
    return { score: chips.reduce((s, c) => s + c.points, 0), chips };
}
/** Most important first; equal points, the longest wait first; every row kept (a stable sort). */
export function rankIssues(rows, score, waitingSince) {
    return rows.map((r, i) => ({ r, i })).sort((a, b) => score(b.r) - score(a.r)
        || Date.parse(waitingSince(a.r).replace(/\+0000$/, 'Z')) - Date.parse(waitingSince(b.r).replace(/\+0000$/, 'Z')) || a.i - b.i).map((x) => x.r);
}
//# sourceMappingURL=priority.js.map